import { withGitpodSize } from "@/assets/icons/utils";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { InputField } from "@/components/podkit/forms/InputField";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/flexkit/tabs/Tabs";
import { TrackLocations } from "@/hooks/use-segment";
import type { PlainWorkflow } from "@/queries/automation-queries";
import { useGetWorkflowWebhookSecret, useRotateWorkflowWebhookSecret } from "@/queries/automation-queries";
import { IconArrowRotateLeftRight } from "central-icons/IconArrowRotateLeftRight";
import { useState, type FC } from "react";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Banner } from "@/components/flexkit/Banner";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { IconGitHub } from "@/assets/icons/IconGitHub";
import { IconGitLab } from "@/assets/icons/geist/IconGitLab";

type ConfigureWebhookDialogProps = {
    workflow: PlainWorkflow;
    open: boolean;
    onClose: () => void;
};

enum Tab {
    GitHub = "github",
    GitLab = "gitlab",
}

export const ConfigureWebhookDialog: FC<ConfigureWebhookDialogProps> = ({ open, onClose, workflow }) => {
    const { toast } = useToast();
    const { data: secretToken, isLoading: isLoadingSecret } = useGetWorkflowWebhookSecret(workflow.id);
    const { mutateAsync: rotateSecret, isPending: isRotating } = useRotateWorkflowWebhookSecret();

    const [selectedPlatform, setSelectedPlatform] = useState<Tab>(Tab.GitHub);

    const payloadUrl = workflow.webhookUrl;
    const contentType = "application/json";

    const handleRotateSecret = async () => {
        try {
            await rotateSecret(workflow.id);
            toast({
                title: "Webhook secret has been rotated",
            });
        } catch (error) {
            toast({
                title: "Failed to rotate webhook secret",
                description: formatError(error),
            });
        }
    };

    const payloadInputField = (
        <InputField
            label="Payload URL"
            hint="Ona uses this endpoint to receive PR events from this automation."
            id="payload-url"
        >
            <CopyableInput
                id="payload-url"
                value={payloadUrl}
                data-testid="payload-url-input"
                data-tracking-id="copy-payload-url-configure-webhook-dialog"
            />
        </InputField>
    );

    const secretInputField = (
        <InputField label="Secret token" hint="Token is generated per trigger." id="secret-token">
            <div className="flex items-center gap-2">
                <CopyableInput
                    id="secret-token"
                    type="password"
                    value={secretToken || ""}
                    disabled={isLoadingSecret}
                    data-testid="secret-token-input"
                    data-tracking-id="copy-secret-token-configure-webhook-dialog"
                    wrapperClassName="grow"
                />
                <Button
                    variant="outline"
                    onClick={handleRotateSecret}
                    disabled={isRotating || isLoadingSecret}
                    loading={isRotating}
                    data-testid="rotate-secret-button"
                    data-tracking-id="rotate-secret-configure-webhook-dialog"
                    LeadingIcon={IconArrowRotateLeftRightWithSize}
                />
            </div>
        </InputField>
    );

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="configure-webhook-dialog"
                data-track-location={TrackLocations.ConfigureWebhookDialog}
            >
                <DialogHeader>
                    <DialogTitle>Configure Webhook</DialogTitle>
                    <DialogDescription>Connect Ona with your GitHub or GitLab repositories.</DialogDescription>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-4">
                    <Tabs value={selectedPlatform} onValueChange={(value) => setSelectedPlatform(value as Tab)}>
                        <TabsList className="w-fit">
                            <TabsTrigger
                                value={Tab.GitHub}
                                data-testid="platform-github"
                                LeadingIcon={IconGitHub}
                                className="min-w-32"
                            >
                                GitHub
                            </TabsTrigger>
                            <TabsTrigger
                                value={Tab.GitLab}
                                data-testid="platform-gitlab"
                                LeadingIcon={IconGitLab}
                                className="min-w-32"
                            >
                                GitLab
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value={Tab.GitHub}>
                            <div className="flex flex-col gap-4 pt-4">
                                <Banner
                                    variant="info"
                                    text={
                                        "Copy the Payload URL and Secret Token into your Github repository's webhook settings."
                                    }
                                    link={
                                        <ExternalLink
                                            href="https://docs.github.com/en/developers/webhooks-and-events/webhooks/creating-webhooks"
                                            className="text-sm font-medium text-content-primary"
                                        >
                                            Learn more
                                        </ExternalLink>
                                    }
                                />
                                <div className="flex flex-col gap-4">
                                    {payloadInputField}
                                    <InputField label="Content Type">
                                        <CopyableInput
                                            value={contentType}
                                            readOnly
                                            data-testid="content-type-input"
                                            data-tracking-id="copy-content-type-webhook-dialog"
                                        />
                                    </InputField>
                                    {secretInputField}
                                </div>
                            </div>
                        </TabsContent>
                        <TabsContent value={Tab.GitLab}>
                            <div className="flex flex-col gap-4 pt-4">
                                <Banner
                                    variant="info"
                                    text={
                                        "Copy the Payload URL and Secret Token into your GitLab repository's webhook settings."
                                    }
                                    link={
                                        <ExternalLink
                                            href="https://docs.gitlab.com/ee/user/project/integrations/webhooks.html"
                                            className="text-sm font-medium text-content-primary"
                                        >
                                            Learn more
                                        </ExternalLink>
                                    }
                                />
                                <div className="flex flex-col gap-4">
                                    {payloadInputField}
                                    {secretInputField}
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="configure-webhook-dialog-cancel">
                            Close
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const IconArrowRotateLeftRightWithSize = withGitpodSize(IconArrowRotateLeftRight);
